import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { addDays, format, isToday, isTomorrow, parseISO } from 'date-fns'
import { Package, Clock, Phone, ChevronRight, ChevronLeft, Search, Check } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { db } from '@/lib/db/database'
import { useUpdateCustomerOrderStatus } from '@/lib/queries/useCustomerOrders'
import { useMyRestaurant } from '@/lib/queries/useRestaurants'
import { useAppStore } from '@/lib/store/useAppStore'
import { strapiGet } from '@/lib/strapi'
import type { CustomerOrder, CustomerOrderStatus } from '@/lib/types/customerOrder'
import type { BookingPreOrderItem } from '@/lib/types/pos'
import { todayISO } from '@/lib/utils/date'

type CollectionStatus = CustomerOrderStatus

function formatMoney(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount)
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`
  }
}

const COLUMN_CONFIG: {
  status: CollectionStatus
  label: string
  color: string
  bg: string
  border: string
}[] = [
  {
    status: 'request',
    label: 'New',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
  {
    status: 'approved',
    label: 'Preparing',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  {
    status: 'arrived',
    label: 'Ready',
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
  },
  {
    status: 'in_delivery',
    label: 'In delivery',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
  },
  {
    status: 'left',
    label: 'Collected',
    color: 'text-gray-500',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
  },
  {
    status: 'no_show',
    label: 'No-show',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  {
    status: 'canceled',
    label: 'Canceled',
    color: 'text-stone-500',
    bg: 'bg-stone-50',
    border: 'border-stone-200',
  },
]

function getCollectionActions(status: CollectionStatus) {
  switch (status) {
    case 'request':
      return [
        { label: 'Accept', targetStatus: 'approved' as CustomerOrderStatus, variant: 'primary' as const },
        { label: 'Cancel', targetStatus: 'canceled' as CustomerOrderStatus, variant: 'ghost' as const },
      ]
    case 'approved':
      return [
        { label: 'Ready', targetStatus: 'arrived' as CustomerOrderStatus, variant: 'primary' as const },
        { label: 'Cancel', targetStatus: 'canceled' as CustomerOrderStatus, variant: 'ghost' as const },
      ]
    case 'arrived':
      return [
        { label: 'Collected', targetStatus: 'left' as CustomerOrderStatus, variant: 'primary' as const },
        { label: 'In delivery', targetStatus: 'in_delivery' as CustomerOrderStatus, variant: 'ghost' as const },
        { label: 'No show', targetStatus: 'no_show' as CustomerOrderStatus, variant: 'ghost' as const },
      ]
    case 'in_delivery':
      return [
        { label: 'Delivered', targetStatus: 'left' as CustomerOrderStatus, variant: 'primary' as const },
        { label: 'No show', targetStatus: 'no_show' as CustomerOrderStatus, variant: 'ghost' as const },
      ]
    default:
      return []
  }
}

function dateLabel(dateStr: string) {
  const d = parseISO(dateStr)
  if (isToday(d)) return 'Today'
  if (isTomorrow(d)) return 'Tomorrow'
  return format(d, 'EEE d MMM')
}

function selectedOptionTotal(selectedOptions: unknown) {
  if (!Array.isArray(selectedOptions)) return 0
  return selectedOptions.reduce((sum, option: any) => {
    const quantity = Math.max(1, Number(option?.quantity) || 1)
    const priceDelta = Number(option?.priceDelta ?? 0)
    return sum + (Number.isFinite(priceDelta) ? priceDelta * quantity : 0)
  }, 0)
}

function preOrderItemBaseTotal(item: BookingPreOrderItem) {
  const base = Number(item.menuItem?.basePrice ?? 0) + Number(item.variant?.priceDelta ?? 0)
  return item.menuItem?.soldByWeight === true && item.weightGrams != null
    ? base * Number(item.weightGrams) / 1000
    : base * item.quantity
}

function preOrderItemTotal(item: BookingPreOrderItem) {
  const options = selectedOptionTotal(item.selectedOptions)
  return item.menuItem?.soldByWeight === true && item.weightGrams != null
    ? preOrderItemBaseTotal(item) + options
    : preOrderItemBaseTotal(item) + options * item.quantity
}

function formatWeight(grams: number) {
  return grams % 1000 === 0 ? `${grams / 1000} kg` : `${grams} g`
}

function selectedOptionLines(selectedOptions: unknown, currency: string) {
  if (!Array.isArray(selectedOptions)) return []
  return selectedOptions
    .map((option: any) => {
      const name = String(option?.name ?? option?.label ?? option?.menuItemName ?? '').trim()
      if (!name) return null
      const quantity = Math.max(1, Number(option?.quantity) || 1)
      const group = typeof option?.group === 'string' && option.group.trim() ? `${option.group.trim()}: ` : ''
      const priceDelta = Number(option?.priceDelta ?? 0)
      const price = Number.isFinite(priceDelta) && priceDelta !== 0
        ? ` ${priceDelta > 0 ? '+' : ''}${formatMoney(priceDelta * quantity, currency)}`
        : ''
      return `${group}${quantity > 1 ? `${quantity}x ` : ''}${name}${price}`
    })
    .filter(Boolean) as string[]
}

function OrderCard({
  order,
  items,
  currency,
  onUpdateStatus,
  advancing,
}: {
  order: CustomerOrder
  items: BookingPreOrderItem[]
  currency: string
  onUpdateStatus: (documentId: string, status: CustomerOrderStatus) => void
  advancing: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const total = items.reduce((s, i) => s + preOrderItemTotal(i), 0)
  const itemCount = items.reduce((s, i) => s + i.quantity, 0)
  const actions = getCollectionActions(order.status as CollectionStatus)

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.18 }}
      className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] shadow-sm"
    >
      <div
        className="flex cursor-pointer select-none items-start gap-3 p-4"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
            {order.guest?.name ?? 'Guest'}
          </p>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
            <Clock className="h-3 w-3 shrink-0" />
            <span>{order.time}</span>
            <span>&middot;</span>
            <span>{dateLabel(order.date)}</span>
          </div>
          {order.guest?.phone && (
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
              <Phone className="h-3 w-3 shrink-0" />
              <span>{order.guest.phone}</span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-xs font-semibold text-[var(--color-text-primary)]">
            {formatMoney(total, currency)}
          </span>
          <span className="text-xs text-[var(--color-text-muted)]">
            {itemCount} item{itemCount !== 1 ? 's' : ''}
          </span>
          {expanded ? (
            <ChevronLeft className="mt-1 h-3.5 w-3.5 rotate-90 text-[var(--color-text-muted)]" />
          ) : (
            <ChevronRight className="mt-1 h-3.5 w-3.5 -rotate-90 text-[var(--color-text-muted)]" />
          )}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="space-y-1 border-t border-[var(--color-border-subtle)] px-4 pb-3 pt-3">
              {items.length === 0 ? (
                <p className="text-xs text-[var(--color-text-muted)]">No items recorded.</p>
              ) : (
                items.map((item) => (
                  <div key={item.documentId} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--color-text-primary)]">
                        <span className="mr-1.5 font-medium text-[var(--color-text-muted)]">
                          {item.weightGrams != null ? formatWeight(item.weightGrams) : `${item.quantity}x`}
                        </span>
                        {item.menuItem?.name ?? '-'}
                        {item.variant?.name ? (
                          <span className="ml-1 text-xs text-[var(--color-text-muted)]">
                            ({item.variant.name})
                          </span>
                        ) : null}
                      </span>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {formatMoney(preOrderItemBaseTotal(item), currency)}
                      </span>
                    </div>
                    {selectedOptionLines(item.selectedOptions, currency).map((optionLine, optionIndex) => (
                      <div key={`${item.documentId}-option-${optionIndex}`} className="mt-0.5 flex items-center justify-between gap-3 pl-5 text-xs text-[var(--color-text-muted)]">
                        <span>+ {optionLine}</span>
                      </div>
                    ))}
                  </div>
                ))
              )}
              {order.comment && (
                <p className="mt-2 border-t border-[var(--color-border-subtle)] pt-1 text-xs italic text-[var(--color-text-muted)]">
                  "{order.comment}"
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {actions.length > 0 && (
        <div className="px-4 pb-4">
          <div className="flex flex-wrap gap-2">
            {actions.map((action) => (
              <Button
                key={action.label}
                size="sm"
                variant={action.variant === 'primary' ? 'primary' : 'ghost'}
                className="min-w-[8rem] flex-1"
                loading={advancing}
                onClick={() => onUpdateStatus(order.documentId, action.targetStatus)}
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default function CollectionsPage() {
  const [search, setSearch] = useState('')
  const [advancingId, setAdvancingId] = useState<string | null>(null)
  const updateStatus = useUpdateCustomerOrderStatus()
  const { data: restaurantResp } = useMyRestaurant()
  const restaurantId = useAppStore((s) => s.restaurantId)

  const restaurant = Array.isArray((restaurantResp as any)?.data) ? (restaurantResp as any).data[0] : null
  const currency = restaurant?.currency ?? 'EUR'

  const allOrders = useLiveQuery(() => db.customerOrders.toArray(), [])
  const allPreOrders = useLiveQuery(() => db.preOrderItems.toArray(), [])

  useEffect(() => {
    if (!restaurantId) return

    let cancelled = false
    let intervalId: number | null = null

    const syncOrders = async () => {
      const dateFrom = todayISO()
      const dateTo = format(addDays(new Date(), 60), 'yyyy-MM-dd')
      const resp = await strapiGet<CustomerOrder[]>('/api/customer-orders', {
        filters: {
          restaurant: { documentId: { $eq: restaurantId } },
          date: { $gte: dateFrom, $lte: dateTo },
        },
        populate: {
          guest: true,
          openingHour: true,
          preOrderItems: { populate: { menuItem: { populate: { category: true, variants: true } }, variant: true } },
        },
        pagination: { pageSize: 500 },
        sort: ['dateTime:asc'],
      })
      if (cancelled) return

      const orders = ((resp as any).data ?? resp) as CustomerOrder[]
      await db.customerOrders.bulkPut(orders)

      const preOrderItems = orders.flatMap((order) =>
        (order.preOrderItems ?? []).map((item) => ({
          ...item,
          orderDocumentId: order.documentId,
        })),
      )
      if (preOrderItems.length > 0) {
        await db.preOrderItems.bulkPut(preOrderItems as BookingPreOrderItem[])
      }
    }

    void syncOrders()
    intervalId = window.setInterval(() => {
      void syncOrders()
    }, 30_000)

    return () => {
      cancelled = true
      if (intervalId != null) window.clearInterval(intervalId)
    }
  }, [restaurantId])

  const collectionOrders = useMemo(() => {
    if (!allOrders) return []
    return allOrders
      .sort((a, b) => {
        const aToday = a.date === todayISO() ? 0 : 1
        const bToday = b.date === todayISO() ? 0 : 1
        if (aToday !== bToday) return aToday - bToday
        return (a.date + a.time).localeCompare(b.date + b.time)
      })
  }, [allOrders])

  const filtered = useMemo(() => {
    if (!search.trim()) return collectionOrders
    const q = search.toLowerCase()
    return collectionOrders.filter(
      (b) =>
        b.guest?.name?.toLowerCase().includes(q) ||
        b.guest?.phone?.toLowerCase().includes(q),
    )
  }, [collectionOrders, search])

  const preOrdersByBooking = useMemo(() => {
    const map = new Map<string, BookingPreOrderItem[]>()
    for (const item of allPreOrders ?? []) {
      const key = item.orderDocumentId ?? ''
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(item)
    }
    return map
  }, [allPreOrders])

  const handleUpdateStatus = async (documentId: string, status: CustomerOrderStatus) => {
    setAdvancingId(documentId)
    try {
      await updateStatus.mutateAsync({ documentId, status })
    } finally {
      setAdvancingId(null)
    }
  }

  const isLoading = allOrders === undefined

  const byStatus = useMemo(() => {
    const map = new Map<CollectionStatus, CustomerOrder[]>()
    for (const col of COLUMN_CONFIG) map.set(col.status, [])
    for (const order of filtered) {
      const status = order.status as CollectionStatus
      if (map.has(status)) map.get(status)!.push(order)
    }
    return map
  }, [filtered])

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Collection Orders"
        description="Manage pickup and delivery handoffs from one queue."
      />

      <div className="relative mb-6 max-w-xs">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
        <input
          placeholder="Search by name or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] pl-9 pr-3 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-accent-amber)]/50 focus:outline-none"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-sm text-[var(--color-text-muted)]">
          Loading...
        </div>
      ) : collectionOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-bg-elevated)]">
            <Package className="h-7 w-7 text-[var(--color-text-muted)]" />
          </div>
          <p className="mb-1 font-semibold text-[var(--color-text-primary)]">No collection orders yet</p>
          <p className="max-w-xs text-sm text-[var(--color-text-muted)]">
            When customers place collection or delivery orders from your booking page, they will appear here.
          </p>
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-1 items-start gap-4 sm:grid-cols-2 xl:grid-cols-7">
          {COLUMN_CONFIG.map((col) => {
            const orders = byStatus.get(col.status) ?? []
            return (
              <div key={col.status} className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${col.bg} ${col.color} ${col.border}`}
                  >
                    {col.label}
                  </span>
                  {orders.length > 0 && (
                    <span className="text-xs font-medium text-[var(--color-text-muted)]">
                      {orders.length}
                    </span>
                  )}
                </div>

                <div className="min-h-[4rem] space-y-3">
                  <AnimatePresence>
                    {orders.length === 0 ? (
                      <div className="flex h-16 items-center justify-center rounded-[var(--radius-lg)] border border-dashed border-[var(--color-border-subtle)]">
                        <span className="text-xs text-[var(--color-text-muted)]">Empty</span>
                      </div>
                    ) : (
                      orders.map((order) => (
                        <OrderCard
                          key={order.documentId}
                          order={order}
                          items={preOrdersByBooking.get(order.documentId) ?? order.preOrderItems ?? []}
                          currency={currency}
                          onUpdateStatus={handleUpdateStatus}
                          advancing={advancingId === order.documentId}
                        />
                      ))
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
